import styled, { css } from 'styled-components';
import { StyledBadge } from '@/components/Badge/styles';
import { breakpoints } from '@/styles/breakpoints';

const StyledFilters = styled.div`
  display: flex;
  justify-content: flex-end;

  ${breakpoints.MD} {
    display: block;
  }
`;

const Toggler = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.primary.second};
  position: relative;

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    path {
      fill: white;
    }
  }

  &:active {
    svg {
      path {
        fill: ${({ theme }) => theme.colors.primary.second};
      }
    }
  }

  ${breakpoints.MD} {
    display: none;
  }
`;

interface WrapperProps {
  opened: boolean;
}

const Wrapper = styled.div<WrapperProps>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  min-height: 100vh;
  min-height: -webkit-fill-available;
  z-index: 100;
  border: 2px solid #fff;
  background-color: ${({ theme }) => theme.colors.bg};
  border-radius: ${({ theme }) => theme.spacings.x7} 0 0
    ${({ theme }) => theme.spacings.x7};
  width: 343px;
  padding: ${({ theme }) => theme.spacings.x7} 0 0
    ${({ theme }) => theme.spacings.x4};
  transform: translateX(100%);
  transition: transform 0.3s ease-in-out;
  will-change: transform;

  ${({ opened }) =>
    opened &&
    css`
      transform: translateX(0);
    `}

  ${breakpoints.MD} {
    transform: none;
    position: static;
    width: auto;
    border: none;
    border-radius: 0;
    padding: 0;
  }
`;

const Close = styled.button`
  position: absolute;
  top: 21px;
  right: 21px;
  height: 24px;
  width: 24px;
  transform: rotate(-45deg);

  ${breakpoints.MD} {
    display: none;
  }

  &:before,
  &:after {
    content: '';
    position: absolute;
    background-color: #fff;
    border-radius: 1px;
    transition: background-color 0.1s ease-in-out;
  }

  &:before {
    height: 2px;
    width: 100%;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
  }

  &:after {
    width: 2px;
    height: 100%;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
  }

  &:hover {
    &:before,
    &:after {
      background-color: ${({ theme }) => theme.colors.secondary.second};
    }
  }

  &:active {
    &:before,
    &:after {
      background-color: ${({ theme }) => theme.colors.primary.first};
    }
  }
`;

const Title = styled.div`
  font-family: PPFormula, sans-serif;
  font-weight: 900;
  font-size: 58px;
  line-height: 50px;
  margin-bottom: ${({ theme }) => theme.spacings.x2};
  text-transform: uppercase;
  letter-spacing: 0.036em;

  ${breakpoints.MD} {
    margin-bottom: ${({ theme }) => theme.spacings.x5};
  }

  ${breakpoints.LG} {
    font-size: 80px;
    line-height: 68px;
    letter-spacing: 0.01em;
    margin-bottom: ${({ theme }) => theme.spacings.x4};
  }
`;

const List = styled.div`
  overflow-y: auto;
  height: calc(100% - 80px);
  padding-right: ${({ theme }) => theme.spacings.x4};

  ${breakpoints.MD} {
    height: auto;
    padding-right: ${({ theme }) => theme.spacings.base};
  }

  ${breakpoints.LG} {
    padding-right: ${({ theme }) => theme.spacings.x4};
  }
`;

const Reset = styled.div`
  padding: ${({ theme }) => theme.spacings.x5} 0;
`;

const GroupHeader = styled.div`
  display: flex;
  border-bottom: 2px solid ${({ theme }) => theme.colors.divider};
  padding: ${({ theme }) => theme.spacings.x2} 0;
  transition: border-color 0.1s ease-in-out;
  transition-delay: 0.3s;
  will-change: border-color;
  align-items: center;

  ${breakpoints.MD} {
    padding: 14px 0;
  }
`;

interface GroupProps {
  expanded: boolean;
  last: boolean;
}

const Group = styled.div<GroupProps>`
  max-height: ${({ theme }) => theme.spacings.x7};
  overflow: hidden;
  transition: max-height 0.3s ease-in-out;

  ${breakpoints.MD} {
    max-height: 52px;
  }

  ${({ expanded }) =>
    expanded &&
    css`
      max-height: 100vh !important;

      ${GroupHeader} {
        border-color: transparent;
        transition-delay: 0s;
      }

      ${GroupExpand} {
        transform: rotate(180deg);
      }
    `}

  ${({ last }) =>
    last &&
    css`
      max-height: 54px;

      ${breakpoints.MD} {
        max-height: 50px;
      }

      ${GroupHeader} {
        border: none;
      }
    `}
`;

const GroupLabel = styled.div`
  font-size: 16px;
  line-height: 22px;
`;

const GroupCount = styled.div`
  margin-left: ${({ theme }) => theme.spacings.x2};
  justify-self: flex-start;

  ${StyledBadge} {
    padding: 2px 11px;
  }
`;

const GroupExpand = styled.button`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 2px solid ${({ theme }) => theme.colors.divider};
  position: relative;
  display: block;
  margin-left: auto;
  transition: transform 0.2s ease-in-out;
  will-change: transform;

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const OptionsWrapper = styled.div`
  border: 2px solid #ffffff;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.spacings.x3};
  isolation: isolate;
`;

const Options = styled.ul`
  list-style-type: none;
  margin: 0;
  padding: ${({ theme }) => theme.spacings.base}
    ${({ theme }) => theme.spacings.x3};
  max-height: 350px;
  overflow-y: auto;
`;

interface OptionProps {
  active: boolean;
}

const Option = styled.li<OptionProps>`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  line-height: 18px;
  padding: ${({ theme }) => theme.spacings.x2} 0;
  border-bottom: 2px solid ${({ theme }) => theme.colors.divider};
  transition: opacity 0.1s ease-in-out;
  will-change: opacity;

  &:last-child {
    border: none;
  }

  ${({ active }) =>
    active &&
    css`
      opacity: 0.5;
    `}
`;

const OptionLabel = styled.span``;

const OptionCount = styled.span``;

export {
  StyledFilters,
  Group,
  GroupLabel,
  OptionsWrapper,
  Options,
  Option,
  OptionLabel,
  OptionCount,
  GroupExpand,
  GroupHeader,
  GroupCount,
  List,
  Reset,
  Title,
  Wrapper,
  Close,
  Toggler,
};
